/* global google */
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useGoogleMapsScript } from './GoogleMapsScriptProvider';
import { translations, Language } from '../data/translations';
import type { Pet, Park } from '../types';
import { config } from '@/config/config';

interface Filters {
  // Access Rules
  offLeash: boolean;
  onLeash: boolean;
  mixedZones: boolean;

  // Safety & Layout
  fullyFenced: boolean;
  partiallyFenced: boolean;
  noFencing: boolean;
  separateAreas: boolean;

  // Amenities
  waterFountain: boolean;
  wasteBags: boolean;
  benches: boolean;
  shade: boolean;
  parking: boolean;
  agilityEquipment: boolean;

  // Environment
  grass: boolean;
  dirt: boolean;
  artificialTurf: boolean;
  flatTerrain: boolean;
  hillyTerrain: boolean;
  quiet: boolean;
  crowded: boolean;
}

interface AppContextType {
  // Language
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (
    key: keyof typeof translations.en,
    vars?: Record<string, string | number>
  ) => string;

  // Theme
  theme: 'light' | 'dark';
  toggleTheme: () => void;

  // View
  view: 'map' | 'list';
  setView: (view: 'map' | 'list') => void;

  // Pets
  pets: Pet[];
  selectedPet: Pet | null;
  setSelectedPet: (pet: Pet | null) => void;

  // Parks
  parks: Park[];
  setParks: (parks: Park[]) => void;
  selectedPark: Park | null;
  setSelectedPark: (park: Park | null) => void;

  // Location (used for map + distance calculations)
  userLocation: { lat: number; lng: number } | null;
  setUserLocation: (location: { lat: number; lng: number } | null) => void;

  // Search center (when user searches postcode/town/address)
  searchCenter: { lat: number; lng: number; label?: string } | null;
  setSearchCenter: (
    center: { lat: number; lng: number; label?: string } | null
  ) => void;
  searchMode: 'text' | 'location';

  // Google Maps loader status
  googleMaps: {
    apiKey: string;
    isLoaded: boolean;
    loadError: Error | undefined;
  };

  // Filters
  filters: Filters;
  setFilters: (filters: Filters) => void;
  clearFilters: () => void;
  activeFilterCount: number;

  // Search
  searchQuery: string;
  setSearchQuery: (query: string) => void;

  // Filtered Parks
  filteredParks: Park[];
}

const defaultFilters: Filters = {
  offLeash: false,
  onLeash: false,
  mixedZones: false,
  fullyFenced: false,
  partiallyFenced: false,
  noFencing: false,
  separateAreas: false,
  waterFountain: false,
  wasteBags: false,
  benches: false,
  shade: false,
  parking: false,
  agilityEquipment: false,
  grass: false,
  dirt: false,
  artificialTurf: false,
  flatTerrain: false,
  hillyTerrain: false,
  quiet: false,
  crowded: false,
};

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{
  children: ReactNode;
  pets?: Pet[];
  initialSelectedPetId?: string | null;
  onSelectedPetChange?: (petId: string | null) => void;
}> = ({
  children,
  pets: petsProp,
  initialSelectedPetId,
  onSelectedPetChange,
}) => {
  const { i18n } = useTranslation();
  const appLang = i18n.resolvedLanguage || i18n.language || '';
  const language: Language = appLang.toLowerCase().startsWith('de')
    ? 'de'
    : appLang.toLowerCase().startsWith('nl')
      ? 'nl'
      : appLang.toLowerCase().startsWith('pl')
        ? 'pl'
        : appLang.toLowerCase().startsWith('es')
          ? 'es'
          : appLang.toLowerCase().startsWith('fr')
            ? 'fr'
            : appLang.toLowerCase().startsWith('it')
              ? 'it'
              : 'en';
  const setLanguage = (lang: Language) => {
    i18n.changeLanguage(lang);
  };
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [view, setView] = useState<'map' | 'list'>('map');
  const pets = petsProp ?? ([] as Pet[]);

  const initialSelectedPet = useMemo(() => {
    if (pets.length === 0) return null;
    if (initialSelectedPetId)
      return pets.find((p) => p.id === initialSelectedPetId) ?? pets[0];
    return pets[0];
  }, [pets, initialSelectedPetId]);

  const [selectedPet, _setSelectedPet] = useState<Pet | null>(
    initialSelectedPet
  );
  const [parks, setParks] = useState<Park[]>([]);
  const [selectedPark, setSelectedPark] = useState<Park | null>(null);
  const [userLocation, setUserLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [searchCenter, setSearchCenter] = useState<{
    lat: number;
    lng: number;
    label?: string;
  } | null>(null);
  const [searchMode, setSearchMode] = useState<'text' | 'location'>('text');
  const [isResolvingSearch, setIsResolvingSearch] = useState(false);
  const [filters, setFilters] = useState<Filters>(defaultFilters);
  const [searchQuery, setSearchQuery] = useState('');

  const { isLoaded: googleMapsLoaded, loadError: googleMapsLoadError } =
    useGoogleMapsScript();

  useEffect(() => {
    _setSelectedPet(initialSelectedPet);
  }, [initialSelectedPet]);

  // Acquire user location once (used as default search center)
  useEffect(() => {
    if (userLocation) return;
    if (!('geolocation' in navigator)) return;

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
      },
      () => {
        // denied/unavailable; fall back to mock parks
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60_000 }
    );
  }, [userLocation]);

  const setSelectedPet = (pet: Pet | null) => {
    _setSelectedPet(pet);
    onSelectedPetChange?.(pet?.id ?? null);
  };

  const t = (
    key: keyof typeof translations.en,
    vars?: Record<string, string | number>
  ): string => {
    const template = translations[language][key] || key;
    if (!vars) return template;

    return Object.entries(vars).reduce((acc, [varKey, varValue]) => {
      return acc.replaceAll(`{{${varKey}}}`, String(varValue));
    }, template);
  };

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const clearFilters = () => {
    setFilters(defaultFilters);
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const computeDistanceKm = (
    a: { lat: number; lng: number },
    b: { lat: number; lng: number }
  ) => {
    const R = 6371;
    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const sinDLat = Math.sin(dLat / 2);
    const sinDLng = Math.sin(dLng / 2);
    const h =
      sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
    const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
    return R * c;
  };

  const isLocationQuery = (geocodeResult: google.maps.GeocoderResult) => {
    const types = geocodeResult.types ?? [];
    const locationish = new Set([
      'postal_code',
      'postal_code_prefix',
      'street_address',
      'route',
      'intersection',
      'neighborhood',
      'sublocality',
      'sublocality_level_1',
      'locality',
      'administrative_area_level_1',
      'administrative_area_level_2',
      'administrative_area_level_3',
      'country',
    ]);
    return types.some((t) => locationish.has(t));
  };

  const mapPlacesResults = (
    center: { lat: number; lng: number },
    results: google.maps.places.PlaceResult[]
  ): Park[] => {
    return results
      .map((r) => {
        const loc = r.geometry?.location;
        if (!loc) return null;
        const coords = { lat: loc.lat(), lng: loc.lng() };
        const distance = computeDistanceKm(center, coords);

        return {
          id: r.place_id ?? `${coords.lat},${coords.lng}`,
          name: r.name ?? 'Dog park',
          address: r.vicinity ?? r.formatted_address ?? '',
          dataSource: 'google',
          coordinates: coords,
          distance: Number(distance.toFixed(1)),
          rating: r.rating ?? 0,
          reviewCount: r.user_ratings_total ?? 0,
          isOpen: r.opening_hours?.open_now ?? false,
          hours: 'See Google for hours',
          verified: false,
          lastUpdated: new Date().toISOString().slice(0, 10),

          // Not provided by Places (kept false)
          offLeash: false,
          onLeash: false,
          mixedZones: false,
          fullyFenced: false,
          partiallyFenced: false,
          separateAreas: false,
          waterFountain: false,
          wasteBags: false,
          benches: false,
          shade: false,
          parking: false,
          agilityEquipment: false,
          surface: ['grass'],
          terrain: 'flat',
          crowdLevel: 'moderate',
          images: (r.photos ?? []).slice(0, 3).map((p) => p.getUrl()),
          description: 'Google Places listing',
          rules: [],
          hazards: [],
          alerts: [],
        };
      })
      .filter((p): p is Park => p !== null)
      .sort((a, b) => a.distance - b.distance);
  };

  const nearbySearchWithExpandingRadius = async (
    center: { lat: number; lng: number },
    keyword: string
  ): Promise<Park[]> => {
    const g = window.google;
    const service = new g.maps.places.PlacesService(
      document.createElement('div')
    );
    const radii = [5000, 10000, 25000, 50000, 100000]; // meters

    const runOnce = (radius: number) =>
      new Promise<google.maps.places.PlaceResult[]>((resolve) => {
        const request: google.maps.places.PlaceSearchRequest = {
          location: center,
          radius,
          keyword,
          type: 'park',
        };
        service.nearbySearch(request, (results, status) => {
          if (
            status !== g.maps.places.PlacesServiceStatus.OK ||
            !results?.length
          ) {
            resolve([]);
            return;
          }
          resolve(results);
        });
      });

    for (const r of radii) {
      const results = await runOnce(r);
      if (results.length > 0) return mapPlacesResults(center, results);
    }
    return [];
  };

  // Default nearby parks around user location (keeps map populated even if user doesn't search)
  useEffect(() => {
    if (!config.googleMapsApiKey) return;
    if (!googleMapsLoaded) return;
    if (!userLocation) return;
    if (searchMode === 'location') return;

    let cancelled = false;
    (async () => {
      const parksFromPlaces = await nearbySearchWithExpandingRadius(
        userLocation,
        'dog park'
      );
      if (cancelled) return;
      if (parksFromPlaces.length > 0) setParks(parksFromPlaces);
    })();

    return () => {
      cancelled = true;
    };
  }, [config.googleMapsApiKey, googleMapsLoaded, userLocation]);

  // When user enters a location-like query, geocode and search parks near that location
  useEffect(() => {
    if (!config.googleMapsApiKey) return;
    if (!googleMapsLoaded) return;

    const q = searchQuery.trim();
    if (!q) {
      setSearchMode('text');
      setSearchCenter(null);
      setIsResolvingSearch(false);
      return;
    }

    setIsResolvingSearch(true);
    const handle = window.setTimeout(() => {
      const g = window.google;
      const geocoder = new g.maps.Geocoder();
      geocoder.geocode({ address: q }, async (results, status) => {
        setIsResolvingSearch(false);
        if (status !== g.maps.GeocoderStatus.OK || !results?.length) {
          setSearchMode('text');
          return;
        }

        const best = results[0];
        if (!isLocationQuery(best)) {
          setSearchMode('text');
          return;
        }

        const loc = best.geometry.location;
        const center = {
          lat: loc.lat(),
          lng: loc.lng(),
          label: best.formatted_address,
        };
        setSearchMode('location');
        setSearchCenter(center);

        const parksFromPlaces = await nearbySearchWithExpandingRadius(
          { lat: center.lat, lng: center.lng },
          'dog park'
        );
        if (parksFromPlaces.length > 0) setParks(parksFromPlaces);
      });
    }, 650);

    return () => window.clearTimeout(handle);
  }, [config.googleMapsApiKey, googleMapsLoaded, searchQuery]);

  // Filter parks based on active filters and search query
  const filteredParks = parks.filter((park) => {
    // Search filter
    // If we successfully treated the query as a location search, don't text-filter by it.
    // While resolving whether this is a location search, don't hide all parks.
    if (!isResolvingSearch && searchMode === 'text' && searchQuery) {
      const query = searchQuery.toLowerCase();
      if (
        !park.name.toLowerCase().includes(query) &&
        !park.address.toLowerCase().includes(query)
      ) {
        return false;
      }
    }

    // Structured filters apply only to parks with structured metadata.
    const hasStructuredFilters = Object.values(filters).some(Boolean);
    const applyStructured =
      hasStructuredFilters && park.dataSource !== 'google';

    // Access Rules
    if (applyStructured) {
      if (filters.offLeash && !park.offLeash) return false;
      if (filters.onLeash && !park.onLeash) return false;
      if (filters.mixedZones && !park.mixedZones) return false;
    }

    // Safety & Layout
    if (applyStructured) {
      if (filters.fullyFenced && !park.fullyFenced) return false;
      if (filters.partiallyFenced && !park.partiallyFenced) return false;
      if (filters.noFencing && park.fullyFenced) return false;
      if (filters.separateAreas && !park.separateAreas) return false;
    }

    // Amenities
    if (applyStructured) {
      if (filters.waterFountain && !park.waterFountain) return false;
      if (filters.wasteBags && !park.wasteBags) return false;
      if (filters.benches && !park.benches) return false;
      if (filters.shade && !park.shade) return false;
      if (filters.parking && !park.parking) return false;
      if (filters.agilityEquipment && !park.agilityEquipment) return false;
    }

    // Environment - Surface
    if (applyStructured) {
      if (filters.grass && !park.surface.includes('grass')) return false;
      if (filters.dirt && !park.surface.includes('dirt')) return false;
      if (filters.artificialTurf && !park.surface.includes('artificialTurf'))
        return false;
    }

    // Environment - Terrain
    if (applyStructured) {
      if (filters.flatTerrain && park.terrain !== 'flat') return false;
      if (filters.hillyTerrain && park.terrain !== 'hilly') return false;
    }

    // Environment - Crowd Level
    if (applyStructured) {
      if (filters.quiet && park.crowdLevel !== 'quiet') return false;
      if (filters.crowded && park.crowdLevel !== 'crowded') return false;
    }

    return true;
  });

  const value: AppContextType = {
    language,
    setLanguage,
    t,
    theme,
    toggleTheme,
    view,
    setView,
    pets,
    selectedPet,
    setSelectedPet,
    parks,
    setParks,
    selectedPark,
    setSelectedPark,
    userLocation,
    setUserLocation,
    searchCenter,
    setSearchCenter,
    searchMode,
    googleMaps: {
      apiKey: config.googleMapsApiKey,
      isLoaded: googleMapsLoaded,
      loadError: googleMapsLoadError,
    },
    filters,
    setFilters,
    clearFilters,
    activeFilterCount,
    searchQuery,
    setSearchQuery,
    filteredParks,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
